import { translateAggregateBackendMessage, translateAxiosErrorMessage } from './masterApiError';

/** Shipment cannot be invoiced when price is zero or missing. */
export function isZeroOrMissingShipmentPrice(price) {
  return price == null || Number(price) === 0;
}

/**
 * Parse POST /api/generate_invoices_for_shipments/ 400 zero-price block.
 * @returns {{ isZeroPrice: boolean, message: string, zeroPriceShipmentIds: number[] }}
 */
export function parseGenerateInvoicesForShipmentsError(error, t) {
  const data = error && error.response && error.response.data;
  const status = error && error.response && error.response.status;
  const ids = data && Array.isArray(data.zero_price_shipment_ids) ? data.zero_price_shipment_ids : [];
  if (status === 400 && ids.length) {
    const rawMessage = data && typeof data.message === 'string' ? data.message.trim() : '';
    const message = rawMessage
      ? translateAggregateBackendMessage(rawMessage, t)
      : getInvoiceGenerationErrorMessage(error, t);
    return { isZeroPrice: true, message, zeroPriceShipmentIds: ids };
  }
  return {
    isZeroPrice: false,
    message: getInvoiceGenerationErrorMessage(error, t),
    zeroPriceShipmentIds: [],
  };
}

/** DRF validation error on manual invoice create/update (`shipments` field). */
export function parseInvoiceShipmentsFieldError(error, t) {
  const data = error && error.response && error.response.data;
  const shipments = data && data.shipments;
  if (Array.isArray(shipments) && shipments.length && typeof shipments[0] === 'string') {
    return translateAggregateBackendMessage(String(shipments[0]).trim(), t);
  }
  return null;
}

function failureLines(linkFailures, whatsappFailures, t) {
  const lines = [];
  linkFailures.forEach((f) => {
    lines.push(
      t('invoiceGeneration.paymentLinkFailureDetail', {
        invoiceId: f.invoice_id,
        gateway: f.gateway || '—',
        error: f.error || t('invoiceGeneration.unknownError'),
      })
    );
  });
  whatsappFailures.forEach((f) => {
    lines.push(
      t('invoiceGeneration.whatsappFailureDetail', {
        invoiceId: f.invoice_id,
        error: f.error || t('invoiceGeneration.unknownError'),
      })
    );
  });
  return lines;
}

/**
 * Build toast/snackbar payload from a 200 invoice generation / regenerate response body.
 * @param {object} data - response.data
 * @param {Function} t - i18next t
 * @param {'create'|'bulk'|'regenerate'} mode
 */
export function getInvoiceGenerationNotification(data, t, mode) {
  const countField = mode === 'regenerate' ? 'regenerated_invoice_count' : 'created_invoice_count';
  const count = data && data[countField] != null ? Number(data[countField]) : 0;
  const linkFailures = data && Array.isArray(data.payment_link_failures) ? data.payment_link_failures : [];
  const whatsappFailures = data && Array.isArray(data.whatsapp_notification_failures)
    ? data.whatsapp_notification_failures
    : [];
  const zeroPriceSkipped = data && Array.isArray(data.zero_price_shipments_skipped)
    ? data.zero_price_shipments_skipped
    : [];
  const zeroPriceMessage = data && data.zero_price_message
    ? String(data.zero_price_message).trim()
    : '';
  const hasLinkFailures = linkFailures.length > 0;
  const hasWhatsappFailures = whatsappFailures.length > 0;
  const hasZeroPriceSkips = zeroPriceSkipped.length > 0 && zeroPriceMessage;

  if (count === 0) {
    if (mode === 'bulk') {
      return {
        type: 'warning',
        message: t('invoiceGeneration.noShipmentsNeeded'),
        refresh: false,
        linkFailures,
        whatsappFailures,
      };
    }
    if (mode === 'regenerate') {
      return {
        type: 'warning',
        message: t('invoiceGeneration.noInvoicesRegenerated'),
        refresh: false,
        linkFailures,
        whatsappFailures,
      };
    }
    return {
      type: 'error',
      message: t('invoiceGeneration.noEligibleShipments'),
      refresh: false,
      linkFailures,
      whatsappFailures,
    };
  }

  let type = 'success';
  let message;

  if (mode === 'regenerate') {
    if (hasLinkFailures) {
      type = 'warning';
      message = t('invoiceGeneration.regeneratedPartialLinkFailures', {
        count,
        failures: linkFailures.length,
      });
    } else {
      message = t('invoiceGeneration.regeneratedSuccess', { count });
    }
  } else if (hasLinkFailures) {
    type = 'warning';
    message = t('invoiceGeneration.createdPartialLinkFailures', {
      count,
      failures: linkFailures.length,
    });
  } else {
    message = t('invoiceGeneration.createdSuccess', { count });
  }

  if (hasWhatsappFailures) {
    message = `${message}\n${t('invoiceGeneration.whatsappFailures', { count: whatsappFailures.length })}`;
  }

  const details = failureLines(linkFailures, whatsappFailures, t);
  if (details.length) {
    message = `${message}\n${details.join('\n')}`;
  }

  if (hasZeroPriceSkips) {
    if (type === 'success') type = 'warning';
    message = `${message}\n${translateAggregateBackendMessage(zeroPriceMessage, t)}`;
  }

  return {
    type,
    message,
    refresh: true,
    linkFailures,
    whatsappFailures,
    zeroPriceShipmentsSkipped: zeroPriceSkipped,
  };
}

export function getInvoiceGenerationErrorMessage(error, t) {
  return translateAxiosErrorMessage(error, t) || t('invoiceGeneration.failed');
}
